import { useCallback, useMemo } from 'react'

import type { ApolloClient } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'

export const CommentsCountsQuery = graphql(`
  query CommentsCountsQuery($input: CommentsCountsInput!) {
    commentsCounts(input: $input) {
      counts {
        channelId
        count
        limit
      }
    }
  }
`)

type CommentsQueryResult = ResultOf<typeof CommentsCountsQuery>

type CommentStatus = NonNullable<
  NonNullable<
    NonNullable<
      VariablesOf<typeof CommentsCountsQuery>['input']['filters']
    >['status']
  >[number]
>

export type CommentsCountFilters = {
  channelIds?: string[]
  postId?: string[]
  status?: CommentStatus[]
  isOwnReply?: boolean
  publishedAt?: {
    start?: string
    end?: string
  }
}

type CommentsQueryVariables = {
  input: {
    organizationId: string
    filters?: CommentsCountFilters
    limit?: number
  }
}

type UseCommentsCountsReturn = {
  fetchCounts: (params: {
    organizationId: string
    filters?: CommentsCountFilters
    limit?: number
  }) => Promise<ApolloClient.QueryResult<CommentsQueryResult>>
}

/**
 * Hook to fetch the comments counts for a given organization and filters.
 * It could be used to fetch the counts with the following filters:
 * - channelIds
 * - postId
 * - status
 * - publishedAt
 *
 * @returns {UseCommentsCountsReturn} An object containing the fetchCounts function
 *
 * @example
 * const { fetchCounts } = useCommentsCounts()
 * const { data, loading, error } = await fetchCounts({
 *   organizationId: '123',
 *   filters: { channelIds: ['123', '456'] },
 * })
 */
export function useCommentsCounts(): UseCommentsCountsReturn {
  const [getCommentCounts] = useLazyQuery<
    CommentsQueryResult,
    CommentsQueryVariables
  >(CommentsCountsQuery, { fetchPolicy: 'no-cache' })

  const fetchCounts = useCallback(
    async (params: {
      organizationId: string
      filters?: CommentsCountFilters
      limit?: number
    }): Promise<ApolloClient.QueryResult<CommentsQueryResult>> => {
      const { organizationId, filters, limit } = params

      return getCommentCounts({
        variables: {
          input: {
            organizationId,
            filters,
            limit,
          },
        },
      })
    },
    [getCommentCounts],
  )

  return useMemo(() => ({ fetchCounts }), [fetchCounts])
}
